import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, ArrowRight } from "lucide-react";

/**
 * PasscodeGate — a soft, keynote-styled passcode wall.
 *
 * Wraps the deck so visitors must enter the passcode before the
 * presentation reveals itself. Successful entry is persisted in
 * localStorage so:
 *   • Refreshing the page doesn't re-prompt
 *   • The Presenter View popup (window.open('/presenter')) inherits
 *     the same auth (localStorage is shared across same-origin windows)
 *
 * Note: this is a client-side gate, intended as a polite barrier for
 * a board-level keynote — not a security boundary. If true server-side
 * auth is needed later, swap this for a backend-issued token.
 */

const PASSCODE = "Niche26*";
const STORAGE_KEY = "niche-keynote-auth-v1";

function isAuthorized() {
  try {
    return typeof window !== "undefined" && window.localStorage.getItem(STORAGE_KEY) === "ok";
  } catch {
    return false;
  }
}

export default function PasscodeGate({ children }) {
  const [authed, setAuthed] = useState(() => isAuthorized());
  const [value, setValue] = useState("");
  const [error, setError] = useState(false);
  const [exiting, setExiting] = useState(false);
  const inputRef = useRef(null);

  // Auto-focus the input when the gate is shown
  useEffect(() => {
    if (!authed && inputRef.current) {
      const t = setTimeout(() => inputRef.current?.focus(), 700);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [authed]);

  function handleSubmit(e) {
    e?.preventDefault?.();
    if (value === PASSCODE) {
      try {
        window.localStorage.setItem(STORAGE_KEY, "ok");
      } catch {
        // ignore storage failures — we'll still let them in for this session
      }
      setExiting(true);
      // Tiny delay so the exit animation can play
      setTimeout(() => setAuthed(true), 700);
    } else {
      setError(true);
      setValue("");
      // Clear the error state after the shake settles so further
      // typing doesn't keep the red ring
      setTimeout(() => setError(false), 1200);
    }
  }

  if (authed) return children;

  return (
    <AnimatePresence>
      {!exiting && (
        <motion.div
          key="passcode-gate"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="fixed inset-0 z-[200] flex items-center justify-center overflow-hidden"
          style={{ background: "rgba(7,18,12,1)" }}
          data-testid="passcode-gate"
        >
          {/* Slow ambient breath */}
          <motion.div
            className="absolute inset-0 pointer-events-none"
            animate={{
              background: [
                "radial-gradient(ellipse at 30% 60%, rgba(150,193,31,0.14) 0%, transparent 55%)",
                "radial-gradient(ellipse at 70% 40%, rgba(0,222,182,0.18) 0%, transparent 55%)",
                "radial-gradient(ellipse at 30% 60%, rgba(150,193,31,0.14) 0%, transparent 55%)",
              ],
            }}
            transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
          />
          {/* Subtle grain */}
          <div className="absolute inset-0 grain pointer-events-none opacity-30" />

          <motion.form
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 20, filter: "blur(20px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            transition={{ duration: 1.0, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="relative z-10 w-full max-w-[520px] px-8 sm:px-0"
          >
            {/* Lock glyph */}
            <motion.div
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="mx-auto w-14 h-14 rounded-2xl flex items-center justify-center mb-8"
              style={{
                background:
                  "linear-gradient(135deg, rgba(150,193,31,0.10) 0%, rgba(0,222,182,0.08) 100%)",
                border: "1px solid rgba(150,193,31,0.40)",
                boxShadow: "0 0 40px rgba(150,193,31,0.25)",
              }}
              data-testid="passcode-glyph"
            >
              <Lock className="w-6 h-6" style={{ color: "#96C11F" }} strokeWidth={2} />
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.6 }}
              className="text-[11px] tracking-[0.32em] uppercase text-center mb-10"
              style={{ color: "rgba(255,255,255,0.55)", fontWeight: 700 }}
            >
              Private screening
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{
                opacity: 1,
                x: error ? [0, -10, 10, -8, 8, -4, 4, 0] : 0,
              }}
              transition={{
                opacity: { duration: 0.6, delay: 0.9 },
                x: { duration: 0.55, ease: "easeInOut" },
              }}
              className="relative"
            >
              <input
                ref={inputRef}
                type="password"
                inputMode="text"
                autoComplete="current-password"
                spellCheck={false}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="••••••••"
                className="w-full text-center px-6 py-5 rounded-xl outline-none transition-all duration-300 tracking-[0.5em] text-2xl"
                style={{
                  background: "rgba(255,255,255,0.04)",
                  border: error
                    ? "1px solid rgba(200,58,79,0.85)"
                    : "1px solid rgba(150,193,31,0.40)",
                  boxShadow: error
                    ? "0 0 50px rgba(200,58,79,0.30)"
                    : "0 0 50px rgba(150,193,31,0.18)",
                  color: "#e2e8f0",
                  fontWeight: 700,
                }}
                data-testid="passcode-input"
              />

              {/* Submit button — sits inside the field on the right */}
              <motion.button
                type="submit"
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.96 }}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-12 h-12 rounded-lg flex items-center justify-center"
                style={{
                  background:
                    "linear-gradient(135deg, #96C11F 0%, #00deb6 100%)",
                  color: "#0d0d0f",
                  boxShadow: "0 6px 28px rgba(150,193,31,0.35)",
                }}
                aria-label="Unlock the presentation"
                data-testid="passcode-submit"
              >
                <ArrowRight className="w-5 h-5" strokeWidth={2.4} />
              </motion.button>
            </motion.div>

            <AnimatePresence>
              {error && (
                <motion.div
                  key="passcode-error"
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                  className="mt-5 text-center text-sm tracking-[0.18em] uppercase"
                  style={{ color: "#ff8597", fontWeight: 700 }}
                  data-testid="passcode-error"
                >
                  Wrong passcode · try again
                </motion.div>
              )}
            </AnimatePresence>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 1.4 }}
              className="mt-12 text-[11px] tracking-[0.32em] uppercase text-center"
              style={{ color: "rgba(255,255,255,0.35)" }}
            >
              Press <span style={{ color: "rgba(255,255,255,0.7)" }}>Enter</span> to unlock
            </motion.div>
          </motion.form>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
